import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Wand2, Loader2, Sparkles, ArrowRight, Gamepad2, Compass } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const CAT_COLOR = {
  economy: "#f59e0b", enemies: "#f43f5e", levels: "#3b9eff", mechanics: "#a855f7",
  world: "#14b8a6", narrative: "#6366f1", polish: "#ec4899",
};

export default function SharedPlan() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`/autopilot/shared/${code}`)
      .then(({ data }) => setPlan(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get(`/games`).then(({ data }) => setGames(Array.isArray(data) ? data : (data.games || []))).catch(() => {});
  }, [code]);

  const runOn = (gid) => navigate(`/app/autopilot/${gid}?plan=${code}`);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  if (notFound || !plan) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="shared-plan-notfound">
        <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[hsl(var(--secondary))] mx-auto mb-4"><Wand2 className="w-6 h-6 text-[hsl(var(--muted-foreground))]" /></div>
        <h1 className="font-display text-xl font-bold">This plan link is invalid or expired</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Ask your friend for a fresh link, or draft your own in the AI Game Director.</p>
        <Button onClick={() => navigate("/app/games")} className="rounded-full mt-5" data-testid="shared-plan-mygames">Go to my games</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="shared-plan">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#a855f7] shrink-0">
          <Wand2 className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Shared Director plan</p>
          <h1 className="font-display text-xl font-extrabold leading-tight">A build plan from {plan.cover_emoji} {plan.game_title}</h1>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6">
        <div className="flex items-start gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
          <p className="text-sm font-semibold" data-testid="shared-plan-goal">{plan.goal}</p>
        </div>
        {plan.overview && <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{plan.overview}</p>}
        <div className="space-y-2" data-testid="shared-plan-steps">
          {(plan.steps || []).map((s, i) => {
            const color = CAT_COLOR[s.category] || CAT_COLOR.mechanics;
            return (
              <div key={s.id || i} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`shared-plan-step-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="grid place-items-center w-6 h-6 rounded-lg text-xs font-bold shrink-0" style={{ background: `${color}22`, color }}>{i + 1}</span>
                  <span className="text-sm font-semibold">{s.title}</span>
                  {s.category && <span className="text-[9px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5" style={{ color, background: `${color}1a` }}>{s.category}</span>}
                </div>
                {s.rationale && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 leading-snug pl-8">{s.rationale}</p>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
        <div className="flex items-center gap-2 mb-1 text-sm font-semibold"><Gamepad2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Run this plan on one of your games</div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">The Director loads these exact steps — tweak the order, preview each, then build. Your game, your version history.</p>
        {games.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">You don't have a game yet — create one first, then come back to this link.</p>
            <Button onClick={() => navigate("/app/create")} className="rounded-full mt-4" data-testid="shared-plan-create">Create a game</Button>
          </div>
        ) : (
          <>
            {games.length > 8 && (
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your games…"
                data-testid="shared-plan-search"
                className="w-full mb-3 rounded-xl border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--primary))]/60" />
            )}
            <div className="grid sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
              {games.filter((g) => (g.title || "").toLowerCase().includes(q.trim().toLowerCase())).map((g) => (
                <button key={g.id} onClick={() => runOn(g.id)} data-testid={`shared-plan-game-${g.id}`}
                  className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] px-3 py-2.5 text-left hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-colors group">
                  <span className="text-xl shrink-0">{g.cover_emoji || g.config?.player?.emoji || "🎮"}</span>
                  <span className="min-w-0 flex-1 text-sm font-medium truncate">{g.title}</span>
                  <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] shrink-0" />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <button onClick={() => navigate("/app/gallery")} data-testid="shared-plan-gallery-link"
        className="mt-5 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] hover:underline font-medium">
        <Compass className="w-3.5 h-3.5" /> Discover more plans in the gallery
      </button>
    </div>
  );
}
